type ActivityChannel = {
  id: string;
  sort_order?: number | null;
  last_activity_at?: string | null;
};

type ActivityPost = {
  channel_id?: string | null;
  created_at?: string | null;
  user_id?: string | null;
};

type ActivityReply = {
  created_at?: string | null;
  user_id?: string | null;
  channel_id?: string | null;
  post?: {
    channel_id?: string | null;
  } | null;
  posts?: {
    channel_id?: string | null;
  } | null;
};

type ActivityMembership = {
  channel_id: string;
  joined_at?: string | null;
  last_read_at?: string | null;
};

export type ChannelActivityState = {
  is_joined: boolean;
  joined_at: string | null;
  last_read_at: string | null;
  last_activity_at: string | null;
  unread_count: number;
};

const toTimestamp = (value?: string | null) => {
  if (!value) {
    return 0;
  }

  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
};

const sortChannelsWithActivityMap = <
  TChannel extends ActivityChannel,
>(
  channels: TChannel[],
  latestActivityByChannel: Map<string, number>,
) =>
  [...channels].sort((left, right) => {
    const rightActivity = latestActivityByChannel.get(right.id) ?? 0;
    const leftActivity = latestActivityByChannel.get(left.id) ?? 0;

    if (rightActivity !== leftActivity) {
      return rightActivity - leftActivity;
    }

    return (left.sort_order ?? Number.MAX_SAFE_INTEGER) - (right.sort_order ?? Number.MAX_SAFE_INTEGER);
  });

export const sortChannelsByRecentActivity = <
  TChannel extends ActivityChannel,
  TPost extends ActivityPost,
>(
  channels: TChannel[],
  posts: TPost[],
) => {
  const latestActivityByChannel = new Map<string, number>();

  posts.forEach((post) => {
    if (!post.channel_id || !post.created_at) {
      return;
    }

    const timestamp = new Date(post.created_at).getTime();
    const current = latestActivityByChannel.get(post.channel_id) ?? 0;
    latestActivityByChannel.set(post.channel_id, Math.max(current, timestamp));
  });

  return sortChannelsWithActivityMap(channels, latestActivityByChannel);
};

const getReplyChannelId = <TReply extends ActivityReply>(reply: TReply) =>
  reply.channel_id ?? reply.post?.channel_id ?? reply.posts?.channel_id ?? null;

export const applyChannelActivityState = <
  TChannel extends ActivityChannel,
  TMembership extends ActivityMembership,
  TPost extends ActivityPost,
  TReply extends ActivityReply,
>(
  channels: TChannel[],
  memberships: TMembership[],
  posts: TPost[],
  replies: TReply[],
  currentUserId?: string | null,
): Array<TChannel & ChannelActivityState> => {
  const latestActivityByChannel = new Map<string, number>();
  const membershipByChannel = new Map(
    memberships
      .filter((membership) => membership.channel_id)
      .map((membership) => [membership.channel_id, membership]),
  );
  const unreadCountByChannel = new Map<string, number>();

  channels.forEach((channel) => {
    const timestamp = toTimestamp(channel.last_activity_at);
    if (timestamp) {
      latestActivityByChannel.set(channel.id, timestamp);
    }
  });

  posts.forEach((post) => {
    if (!post.channel_id || !post.created_at) {
      return;
    }

    const timestamp = toTimestamp(post.created_at);
    latestActivityByChannel.set(
      post.channel_id,
      Math.max(latestActivityByChannel.get(post.channel_id) ?? 0, timestamp),
    );

    if (!currentUserId || post.user_id === currentUserId) {
      return;
    }

    const membership = membershipByChannel.get(post.channel_id);
    if (!membership) {
      return;
    }

    const cutoff = Math.max(
      toTimestamp(membership.last_read_at),
      toTimestamp(membership.joined_at),
    );

    if (timestamp > cutoff) {
      unreadCountByChannel.set(post.channel_id, (unreadCountByChannel.get(post.channel_id) ?? 0) + 1);
    }
  });

  replies.forEach((reply) => {
    const channelId = getReplyChannelId(reply);
    if (!channelId || !reply.created_at) {
      return;
    }

    const timestamp = toTimestamp(reply.created_at);
    latestActivityByChannel.set(
      channelId,
      Math.max(latestActivityByChannel.get(channelId) ?? 0, timestamp),
    );

    if (!currentUserId || reply.user_id === currentUserId) {
      return;
    }

    const membership = membershipByChannel.get(channelId);
    if (!membership) {
      return;
    }

    const cutoff = Math.max(
      toTimestamp(membership.last_read_at),
      toTimestamp(membership.joined_at),
    );

    if (timestamp > cutoff) {
      unreadCountByChannel.set(channelId, (unreadCountByChannel.get(channelId) ?? 0) + 1);
    }
  });

  const sortedChannels = sortChannelsWithActivityMap(channels, latestActivityByChannel);

  return sortedChannels.map((channel) => {
    const membership = membershipByChannel.get(channel.id);
    const lastActivityAt = latestActivityByChannel.get(channel.id) ?? 0;

    return {
      ...channel,
      is_joined: Boolean(membership),
      joined_at: membership?.joined_at ?? null,
      last_read_at: membership?.last_read_at ?? null,
      last_activity_at: lastActivityAt ? new Date(lastActivityAt).toISOString() : null,
      unread_count: unreadCountByChannel.get(channel.id) ?? 0,
    };
  });
};
